import { useContext, useEffect, useMemo, useState } from 'react'
import { collection, onSnapshot, query, where } from 'firebase/firestore'
import { ChecklistDetailDialog } from '../ChecklistBoard/ChecklistDetailDialog'
import { firestoreDb } from '../../services/firebase'
import type { ChecklistItem } from '../../types/schema'
import { WorkspaceContext } from '../../context/workspaceContextCore'
import { scopedReadConstraints } from '../../services/accessScopeService'

type ManageChecklistDetailSurfaceProps = {
  checklistId: string
  parentContainerId: string
  checklistItems: ChecklistItem[]
}

const canonicalBoardDocId = (parentContainerId: string, nestedChecklistId: string) =>
  `${parentContainerId}_${nestedChecklistId}`.replaceAll('/', '_')

export const ManageChecklistDetailSurface = ({
  checklistId,
  parentContainerId,
  checklistItems,
}: ManageChecklistDetailSurfaceProps) => {
  const workspaceContext = useContext(WorkspaceContext)
  const workspaceId = useMemo(
    () =>
      checklistItems.find((item) => item.id === checklistId)?.workspaceId ??
      checklistItems[0]?.workspaceId ??
      workspaceContext?.activeWorkspaceId ??
      null,
    [checklistId, checklistItems, workspaceContext?.activeWorkspaceId],
  )
  const directItemId = useMemo(
    () => checklistItems.find((item) => item.id === checklistId)?.id ?? null,
    [checklistId, checklistItems],
  )
  const [resolvedItemId, setResolvedItemId] = useState<string | null>(directItemId)
  const [isResolving, setIsResolving] = useState(!directItemId)

  const [prevDirectItemId, setPrevDirectItemId] = useState<string | null>(directItemId)
  if (directItemId !== prevDirectItemId) {
    setPrevDirectItemId(directItemId)
    setResolvedItemId(directItemId)
    setIsResolving(!directItemId)
  }

  useEffect(() => {
    if (directItemId || !workspaceId) {
      return
    }
    const fallbackId = canonicalBoardDocId(parentContainerId, checklistId)
    const itemQuery = query(
      collection(firestoreDb, 'checklists'),
      where('workspaceId', '==', workspaceId),
      where('parentContainerId', '==', parentContainerId),
      where('sourceChecklistId', '==', checklistId),
      ...scopedReadConstraints(workspaceId),
    )
    return onSnapshot(
      itemQuery,
      (snapshot) => {
        setResolvedItemId(snapshot.docs[0]?.id ?? fallbackId)
        setIsResolving(false)
      },
      () => {
        setResolvedItemId(fallbackId)
        setIsResolving(false)
      },
    )
  }, [checklistId, directItemId, parentContainerId, workspaceId])

  useEffect(() => {
    let rafId: number | null = null
    const handleFocusField = (event: Event) => {
      const customEvent = event as CustomEvent<{ nodeId?: string; field?: string }>
      const detail = customEvent.detail
      if (!detail || (detail.nodeId !== checklistId && detail.nodeId !== resolvedItemId)) return

      if (rafId != null) cancelAnimationFrame(rafId)
      rafId = requestAnimationFrame(() => {
        const selector = detail.field
          ? `[data-field="${detail.field}"][data-node-id="${checklistId}"]`
          : `[data-node-id="${checklistId}"]`
        const target = document.querySelector<HTMLElement>(selector)
        if (target) {
          target.scrollIntoView({ block: 'center', behavior: 'smooth' })
          const innerInput = target.querySelector<HTMLInputElement | HTMLTextAreaElement>(
            'input, textarea, select, [contenteditable="true"]',
          ) ?? (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement ? target : null)
          innerInput?.focus()
        }
      })
    }

    window.addEventListener('detailedView:focusField', handleFocusField)
    return () => {
      window.removeEventListener('detailedView:focusField', handleFocusField)
      if (rafId != null) cancelAnimationFrame(rafId)
    }
  }, [checklistId, resolvedItemId])

  if (isResolving) {
    return (
      <div className="manageChecklistDetailSurface" data-field="text" data-node-id={checklistId}>
        <div className="detailedFormEmpty">Loading checklist details…</div>
      </div>
    )
  }

  if (!resolvedItemId) {
    return (
      <div className="manageChecklistDetailSurface" data-field="text" data-node-id={checklistId}>
        <div className="detailedFormEmpty">Checklist details are not available for this item yet.</div>
      </div>
    )
  }

  return (
    <div className="manageChecklistDetailSurface" data-field="text" data-node-id={checklistId}>
      <ChecklistDetailDialog
        open
        presentation="embedded"
        itemId={resolvedItemId}
        mode="edit"
        onDismiss={() => undefined}
      />
    </div>
  )
}
